// /Users/rd/CaseManageVue/src/App.vue

<template>
  <div id="app">
    <AuditHealthBanner />
    <div v-if="authStore.accessTokenStale" class="access-banner" role="status">
      <p>Sign-in did not finish loading your access. Reload the page.</p>
      <button type="button" @click="reloadForAccess">Reload</button>
    </div>
    <div id="main-content">
      <!-- Authenticated pages get the global shell (hamburger menu on every
           page); bare pages (login, signed-out) render the view alone. -->
      <AppLayout v-if="showAppChrome">
        <router-view />
      </AppLayout>
      <router-view v-else />
    </div>
    <!-- Debug Role Switcher - Hidden by default, show with Ctrl+Shift+D.
         Loaded only in non-hardened builds (role spoofing risk). -->
    <UserRoleSwitcher v-if="debugMenuEnabled && showDebugRoleSwitcher" />
    <!-- Debug Access Info - Shows admin users how to access debug tools -->
    <DebugAccessInfo v-if="debugMenuEnabled" />
    <!-- Smoke test launcher (dev app only; stubbed to null in hardened builds).
         Floating button so non-admin roles can run the per-role smoke suite. -->
    <SmokeTestLauncher />
    <!-- Workspace demo role rotator (allowlisted emails on hosted dev only). -->
    <WorkspaceRoleRotator />
    <SessionTimeoutDialog />
    <PrivacyBlurOverlay />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, defineAsyncComponent } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/store/authStore'
import AppLayout from '@/layouts/AppLayout.vue'
import SessionTimeoutDialog from '@/components/SessionTimeoutDialog.vue'
import PrivacyBlurOverlay from '@/components/PrivacyBlurOverlay.vue'
import AuditHealthBanner from '@/components/AuditHealthBanner.vue'
import { usePrivacyBlur } from '@/composables/usePrivacyBlur'
import { useDebugToolsSettings } from '@/composables/useDebugToolsSettings'
import { SmokeTestLauncher, WorkspaceRoleRotator } from '@dev-only'
import { useThemeInitializer } from '@/composables/useThemeInitializer'

// Keep this as a compile-time constant so hardened builds can drop the debug
// components from the bundle instead of merely hiding them at runtime.
const debugMenuEnabled = import.meta.env.VITE_PROD_HARDENED !== true
const NullComponent = { name: 'ProdHardenedNull', render: () => null }
const UserRoleSwitcher = debugMenuEnabled
  ? defineAsyncComponent(() => import('@/components/UserRoleSwitcher.vue'))
  : NullComponent
const DebugAccessInfo = debugMenuEnabled
  ? defineAsyncComponent(() => import('@/components/DebugAccessInfo.vue'))
  : NullComponent

const showDebugRoleSwitcher = ref(false)

// Global nav chrome appears on every authenticated page except routes that opt
// out with meta.layout === 'bare'. Requires a role (not just a session) so the
// menu never renders half-populated during auth init.
const route = useRoute()
const authStore = useAuthStore()
const showAppChrome = computed(() => route.meta.layout !== 'bare' && !!authStore.currentUser?.role)

// Initialize theme
useThemeInitializer()

// Start privacy blur listener (settings from Firestore when signed in)
usePrivacyBlur()
useDebugToolsSettings()

// Keyboard shortcut to show/hide debug role switcher: Ctrl+Shift+D
// Gated to non-hardened builds — the switcher can spoof the client role, so
// it must not exist at all in production bundles.
const handleKeydown = (event) => {
  if (!debugMenuEnabled) return
  if (event.ctrlKey && event.shiftKey && event.key === 'D') {
    event.preventDefault()
    showDebugRoleSwitcher.value = !showDebugRoleSwitcher.value
  }
}

function hideDebugRoleSwitcher() {
  showDebugRoleSwitcher.value = false
}

function reloadForAccess() {
  window.location.reload()
}

onMounted(() => {
  if (debugMenuEnabled) {
    document.addEventListener('keydown', handleKeydown)
    window.addEventListener('casemanage:hide-debug-role-switcher', hideDebugRoleSwitcher)
  }
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('casemanage:hide-debug-role-switcher', hideDebugRoleSwitcher)
})
</script>

<style>
/* Global styles */
#app {
  min-height: 100vh;
  background: var(--bg-primary, #e8ecf1);
}

#main-content {
  padding: 0 2rem;
}

/* Phones: 2rem gutters eat ~64px of a 375px screen. This must live here (not
   only in layout.css) — this block loads after it, so it wins the cascade. */
@media (max-width: 768px) {
  #main-content {
    padding: 0 var(--spacing-md, 1rem);
  }
}

.access-banner {
  position: sticky;
  top: 0;
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;
  /* Clear the fixed top-right menu button. */
  padding: 0.75rem 4.5rem 0.75rem 2rem;
  background: #fef3c7;
  border-bottom: 1px solid #d97706;
  color: #78350f;
}

.access-banner p {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.4;
}

.access-banner button {
  flex-shrink: 0;
  padding: 0.4rem 0.9rem;
  border: 1px solid #b45309;
  border-radius: 6px;
  background: #fff;
  color: #78350f;
  font: inherit;
  font-size: 0.9rem;
  cursor: pointer;
}

.access-banner button:hover {
  background: #fffbeb;
}

@media (max-width: 768px) {
  .access-banner {
    padding: 0.75rem 1rem;
    flex-wrap: wrap;
  }
}

/* Hide nav chrome in print (#nav is legacy; the rest is the global menu) */
@media print {
  .access-banner,
  #nav,
  .hamburger-btn,
  .nav-panel,
  .nav-overlay {
    display: none !important;
  }
}
</style>
